import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { PortalConfig } from './types';
import type { PortalType } from '@shared/types/database';

const PortalConfigContext = createContext<PortalConfig | null>(null);

interface PortalConfigProviderProps {
  config: PortalConfig;
  children: ReactNode;
}

export function PortalConfigProvider({ config, children }: PortalConfigProviderProps) {
  return (
    <PortalConfigContext.Provider value={config}>
      {children}
    </PortalConfigContext.Provider>
  );
}

export function usePortalConfig(): PortalConfig {
  const context = useContext(PortalConfigContext);
  if (!context) {
    throw new Error('usePortalConfig must be used within PortalConfigProvider');
  }
  return context;
}

/**
 * Returns the current portal ID as a single-element array.
 * Used for filtering data to only show cases from the current portal.
 *
 * Note: Uses useMemo to ensure stable array reference between renders.
 */
export function usePortalIds(): PortalType[] {
  const config = usePortalConfig();
  return useMemo(
    () => [config.id],
    [config.id]
  );
}
